import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoJsonLd from "@/components/feature/SeoJsonLd";
import { SITE_URL } from "@/constants/site";

interface FAQ {
  question: string;
  answer: string;
}

interface ComparisonTable {
  headers: string[];
  rows: string[][];
}

interface ArticleSection {
  heading: string;
  paragraphs?: string[];
  comparisonTable?: ComparisonTable;
  faqs?: FAQ[];
}

interface SEOArticleLayoutProps {
  slug: string;
  title: string;
  metaDescription: string;
  publishDate: string;
  readTime: string;
  badge: string;
  heroImage: string;
  sections: ArticleSection[];
  relatedArticles: { label: string; to: string; external?: boolean }[];
}

function buildArticleJsonLd(props: SEOArticleLayoutProps) {
  const url = SITE_URL + "/" + props.slug;
  const faqSections = props.sections.filter((s) => s.faqs && s.faqs.length > 0);
  const graph: Record<string, unknown>[] = [
    {
      "@type": "Article",
      "headline": props.title,
      "description": props.metaDescription,
      "image": props.heroImage,
      "datePublished": props.publishDate,
      "dateModified": "2026-07-12",
      "author": {
        "@type": "Organization",
        "name": "Hipobuy Spreadsheet",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "Hipobuy Spreadsheet",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/16c9e81c106f6a445869e4a45760203f.jpeg"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      }
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": props.title,
          "item": url
        }
      ]
    }
  ];

  if (faqSections.length > 0) {
    const allFaqs: { question: string; answer: string }[] = [];
    faqSections.forEach((s) => {
      if (s.faqs) allFaqs.push(...s.faqs);
    });
    graph.push({
      "@type": "FAQPage",
      "mainEntity": allFaqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph
  };
}

export default function SEOArticleLayout(props: SEOArticleLayoutProps) {
  const articleJsonLd = buildArticleJsonLd(props);

  return (
    <>
      <SeoJsonLd data={articleJsonLd} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </nav>

          <div className="relative rounded-2xl overflow-hidden aspect-[2/1] mb-8">
            <img
              src={props.heroImage}
              alt={props.title}
              title={props.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
              <span className="inline-block px-3 py-1 rounded-full bg-background-50/90 text-primary-700 text-xs font-semibold uppercase tracking-wider">
                {props.badge}
              </span>
            </div>
          </div>

          <header className="mb-10">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading text-foreground-900 leading-tight mb-4">
              {props.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500">
              Published: {props.publishDate} &middot; {props.readTime} &middot; Updated regularly
            </p>
          </header>

          <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">
            {props.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-4">
                  {section.heading}
                </h2>

                {section.paragraphs && section.paragraphs.map((p, pi) => (
                  <p key={pi} className="text-sm md:text-base mt-3 first:mt-0">
                    {p}
                  </p>
                ))}

                {section.comparisonTable && (
                  <div className="overflow-x-auto mt-4 rounded-xl border border-background-200/60">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-secondary-50">
                          {section.comparisonTable.headers.map((h, hi) => (
                            <th
                              key={hi}
                              className={`text-left py-3 px-4 text-sm font-semibold ${
                                hi === 0
                                  ? "text-foreground-800"
                                  : hi === 1
                                  ? "text-primary-700"
                                  : "text-foreground-600"
                              }`}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.comparisonTable.rows.map((row, ri) => (
                          <tr
                            key={ri}
                            className="border-t border-background-200/40 hover:bg-background-50 transition-colors"
                          >
                            {row.map((cell, ci) => (
                              <td
                                key={ci}
                                className={`py-3 px-4 text-sm ${
                                  ci === 0
                                    ? "font-medium text-foreground-800"
                                    : ci === 1
                                    ? "text-primary-700"
                                    : "text-foreground-600"
                                }`}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {section.faqs && (
                  <div className="space-y-3 mt-4">
                    {section.faqs.map((faq, fi) => (
                      <div
                        key={fi}
                        className="p-5 rounded-2xl bg-background-100 border border-background-200/60"
                      >
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">
                          {faq.question}
                        </h3>
                        <p className="text-sm text-foreground-600">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>

          <div className="mt-12 p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-200/60">
            <h3 className="text-lg md:text-xl font-heading text-foreground-900 mb-3 text-center">
              Explore More Resources
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto text-center">
              Continue your research with our other detailed guides and comparison pages.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              {props.relatedArticles.map((rel, ri) => (
                rel.external ? (
                  <a
                    key={ri}
                    href={rel.to}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 rounded-lg bg-background-100 border border-background-200/60 text-sm text-primary-700 font-medium hover:bg-primary-50 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {rel.label}
                  </a>
                ) : (
                  <Link
                    key={ri}
                    to={rel.to}
                    className="px-4 py-2 rounded-lg bg-background-100 border border-background-200/60 text-sm text-primary-700 font-medium hover:bg-primary-50 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {rel.label}
                  </Link>
                )
              ))}
            </div>
          </div>

          <div className="mt-12 p-6 md:p-8 rounded-2xl bg-primary-500 text-white text-center">
            <h3 className="text-lg md:text-xl font-heading mb-3">
              Ready to Start Shopping Smarter?
            </h3>
            <p className="text-sm text-white/80 mb-5 max-w-md mx-auto">
              Browse organized product categories and discover your next great find on the main platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-xl bg-white text-primary-700 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Visit Main Platform
                <i className="ri-external-link-line ml-2"></i>
              </a>
              <Link
                to="/"
                className="px-6 py-3 rounded-xl border-2 border-white/60 text-white text-sm font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2 text-xs text-foreground-400">
            <span>Related:</span>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">Hipobuy Spreadsheet Home</Link>
            <span>&middot;</span>
            <Link to="/hipobuy-spreadsheet-complete-guide" className="text-primary-600 hover:text-primary-700 cursor-pointer">Complete Guide</Link>
            <span>&middot;</span>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 cursor-pointer">Main Platform</a>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}